import * as React from 'react';
import { connect } from 'react-redux';
import { UpdateField } from '@webapp/store/release/action-creators';
import AppState from '../../../store/state';
import { Dispatch } from 'redux';

import BoxSelector from '@components/actions/box-selector';
import SelectorWrapper from '@components/layout/selector-wrapper';
import BoxHeader from '@components/layout/box-header';

import { Category } from '@common/types/categories/category';
import CategorySearchCtrl from '../../categories/category-search';
import { ReleaseGenre } from '@common/types/release/release-genre';
import { UpdateArtistPresetsField } from '../../../store/artist-presets/action-creators';
import { Translate } from 'react-localize-redux';

interface GenresSelectorCtrlProps {
  className?: string;

  section?: 'presets' | 'release';
}

interface StateProps {
  list: Category<ReleaseGenre>[];
  active: ReleaseGenre[];
  updateGenres: (
    genres: ReleaseGenre[]
  ) => UpdateField<'genres'> | UpdateArtistPresetsField<'genres'>;
}

const sortPopular = (a: Category<ReleaseGenre>, b: Category<ReleaseGenre>) => {
  if (a.metadata.isPopular === b.metadata.isPopular) {
    return a.name.localeCompare(b.name);
  }

  if (a.metadata.isPopular) {
    return -1;
  }

  return 1;
};

const mapStateToProps = (state: AppState, props: GenresSelectorCtrlProps) => ({
  list: state.categories.lists.genre?.sort(sortPopular) || [],
  active:
    props.section === 'presets'
      ? state.presets.presets?.genres || []
      : state.release.release?.genres || [],
});

const mapDispatchToProps = (
  dispatch: Dispatch,
  props: GenresSelectorCtrlProps
) => ({
  updateGenres:
    props.section === 'presets'
      ? (genres: ReleaseGenre[]) =>
          dispatch(new UpdateArtistPresetsField<'genres'>('genres', genres))
      : (genres: ReleaseGenre[]) =>
          dispatch(new UpdateField<'genres'>('genres', genres)),
});

export const GenresSelectorCtrl: React.FC<
  GenresSelectorCtrlProps & StateProps
> = (props) => {
  const [newGenre, updateNewGenre] = React.useState('');
  const [error, setError] = React.useState('');
  const [showSearch, setShowSearch] = React.useState(false);

  React.useEffect(() => {
    if (!newGenre && error) {
      setError('');
    }
  }, [newGenre, error]);

  const getStatus = (genre: ReleaseGenre) => {
    const idx = props.active.findIndex((g) => genre.value === g.value);
    if (idx === 0) {
      return 'primary';
    }
    if (idx > 0) {
      return 'secondary';
    }
    return undefined;
  };

  const getList = () => {
    const partial = props.list.slice(0, 10);
    const missing = props.active.filter(
      (a) => !partial.find((b) => a.value === b.metadata?.value)
    );
    const selected = missing.map((g) =>
      props.list.find((o) => o.metadata?.value === g.value)
    );

    return partial.concat(
      selected.filter((o) => o !== undefined) as Category<ReleaseGenre>[]
    );
  };

  const handleSelection = (value: string) => {
    let newList: ReleaseGenre[] = [];

    const item = props.list.find((a) => a.metadata?.value === value)?.metadata;

    if (!item) {
      return;
    }

    if (props.active.find((i) => i.value === item.value)) {
      newList = props.active.filter((g) => g.value !== item.value);
    } else {
      newList = props.active.concat([item]);
    }

    if (newList.length > 2) {
      newList = newList.filter((_, i) => i !== 1);
    }

    props.updateGenres(newList as ReleaseGenre[]);
  };

  const handleSearchSelection = (value: string) => {
    updateNewGenre('');
    return handleSelection(value);
  };

  const handleBlur = () => {
    if (newGenre) {
      setError('Genre not found');
    }
  };

  const handleFocus = () => {
    setError('');
  };

  return (
    <div className={props.className}>
      <BoxHeader
        title={<Translate id="ARTIST_PROFILE.PRESETS.GENRE.TITLE" />}
        text={<Translate id="ARTIST_PROFILE.PRESETS.GENRE.TEXT" />}
        onClick={() => setShowSearch(!showSearch)}
      />
      <SelectorWrapper>
        {getList().map((c, i) => (
          <BoxSelector
            className="selector"
            key={`genre-${i}`}
            status={getStatus(c.metadata as ReleaseGenre)}
            onClick={() => handleSelection(c.metadata?.value as string)}
          >
            {c.name}
          </BoxSelector>
        ))}
      </SelectorWrapper>
      <CategorySearchCtrl
        className="mt-20"
        label={<Translate id="ARTIST_PROFILE.PRESETS.GENRE.INPUT.LABEL" />}
        service="genre"
        value={newGenre}
        error={error}
        fullList={props.list.map((t) => t.name)}
        filterList={(value) =>
          value.toLowerCase().includes(newGenre.toLowerCase())
        }
        onChange={(value: string) => updateNewGenre(value)}
        onSelection={(value: string) => handleSearchSelection(value)}
        onFocus={() => handleFocus()}
        onBlur={() => handleBlur()}
      />
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(GenresSelectorCtrl);
